import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import { 
  ArchiveBoxIcon,
  ShoppingBagIcon,
  TagIcon,
  QrCodeIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ShoppingCartIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const StockPage = () => {
  const { t } = useLanguage();
  const { 
    getProducts, 
    getLowStockProducts, 
    addNotification, 
    selectedProduct, 
    setSelectedProduct 
  } = useAppData();
  const [activeTab, setActiveTab] = useState('inventory');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Récupérer les produits depuis le contexte global
  const mockProducts = getProducts();
  
  // Catégories disponibles
  const categories = [
    { value: 'all', label: 'Toutes les catégories' },
    { value: 'supplements', label: 'Suppléments' },
    { value: 'clothing', label: 'Vêtements' },
    { value: 'accessories', label: 'Accessoires' },
    { value: 'equipment', label: 'Équipements' },
    { value: 'drinks', label: 'Boissons' }
  ];

  // Statistiques du stock
  const stockStats = [
    {
      title: 'Total Produits',
      value: mockProducts.length,
      icon: ArchiveBoxIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Stock Faible',
      value: getLowStockProducts().length,
      icon: ExclamationCircleIcon,
      color: 'from-red-500 to-red-600',
    },
    {
      title: 'Valeur Totale',
      value: mockProducts.reduce((sum, product) => sum + (product.price * product.stock), 0).toLocaleString('fr-DZ') + ' DA',
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-green-600',
    },
  ];

  // Distribution du stock par catégorie
  const stockDistribution = categories
    .filter(c => c.value !== 'all')
    .map(category => {
      const products = mockProducts.filter(p => p.category === category.value);
      const count = products.length;
      const percentage = (count / mockProducts.length * 100).toFixed(1);
      
      return {
        name: category.label,
        count,
        percentage,
        color: category.value === 'supplements' ? 'from-blue-400 to-blue-500' :
               category.value === 'clothing' ? 'from-purple-400 to-purple-500' :
               category.value === 'accessories' ? 'from-amber-400 to-amber-500' :
               category.value === 'equipment' ? 'from-green-400 to-green-500' :
               'from-red-400 to-red-500'
      };
    });

  // Onglets
  const tabs = [
    {
      id: 'inventory',
      name: 'Inventaire',
      icon: ArchiveBoxIcon,
      description: 'Gestion du stock'
    },
    {
      id: 'products',
      name: 'Produits',
      icon: ShoppingBagIcon,
      description: 'Catalogue des produits'
    },
    {
      id: 'categories',
      name: 'Catégories',
      icon: TagIcon,
      description: 'Gestion des catégories'
    },
    {
      id: 'orders',
      name: 'Commandes',
      icon: ShoppingCartIcon,
      description: 'Suivi des commandes'
    }
  ];

  // Filtrage des produits
  const filteredProducts = mockProducts
    .filter(product => product.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(product => categoryFilter === 'all' || product.category === categoryFilter);

  // Vérifier les produits à stock faible au chargement
  useEffect(() => {
    const lowStockItems = getLowStockProducts();
    if (lowStockItems.length > 0) {
      // Notification contextuelle uniquement si on est sur un autre onglet que l'inventaire
      if (activeTab !== 'inventory') {
        addNotification({
          type: 'warning',
          title: 'Produits en stock faible',
          message: `${lowStockItems.length} produits nécessitent un réapprovisionnement`,
          link: '/stock?tab=inventory'
        });
      }
    }
  }, [activeTab, getLowStockProducts, addNotification]);

  // Gestion de l'ajout/modification de produit (mock)
  const handleSaveProduct = (productData) => {
    console.log('Sauvegarde du produit:', productData);
    
    // Notifier le succès
    addNotification({
      type: 'success',
      title: 'Produit sauvegardé',
      message: `Le produit ${productData.name || 'nouveau'} a été sauvegardé avec succès`,
      link: '/stock'
    });
    
    setShowAddForm(false);
  };

  // Gestion de la suppression de produit (mock)
  const handleDeleteProduct = (id) => {
    console.log('Suppression du produit avec ID:', id);
    
    // Notifier la suppression
    addNotification({
      type: 'info',
      title: 'Produit supprimé',
      message: `Le produit a été supprimé avec succès`,
      link: '/stock'
    });
    
    setIsDeleteModalOpen(false);
    setProductToDelete(null);
  };

  // Fonction pour ouvrir la modal de suppression
  const openDeleteModal = (product) => {
    setProductToDelete(product);
    setIsDeleteModalOpen(true);
  };

  // Fonction pour modifier un produit
  const editProduct = (product) => {
    setSelectedProduct(product);
    setShowAddForm(true);
  };

  // Fonction modifiée pour voir le détail d'un produit
  const viewProduct = (product) => {
    setSelectedProduct(product); // Utilise le contexte global
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Gestion du Stock
          </h1>
          <p className="text-white/60 mt-2">
            Gérez votre inventaire et vos produits
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedProduct(null);
            setShowAddForm(true);
          }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouveau Produit</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stockStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Répartition par catégorie */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">
          Répartition par Catégorie
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {stockDistribution.map((item) => (
            <div 
              key={item.name}
              className="bg-white/5 rounded-xl p-4 border border-white/10"
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-white">{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                  {item.count}
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div 
                  className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
              <p className="mt-2 text-sm text-white/60">
                {item.percentage}% des produits
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Content based on active tab */}
      {activeTab === 'inventory' && (
        <div className="space-y-6">
          {/* Search and Filter Bar */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category Filter */}
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-white/50" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Inventory Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl overflow-hidden border border-white/20 hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-medium text-white">{product.name}</h3>
                      <p className="text-white/60 text-sm">
                        {categories.find(c => c.value === product.category)?.label || product.category}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => editProduct(product)}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4 text-white/70" />
                      </button>
                      <button 
                        onClick={() => openDeleteModal(product)}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4 text-white/70 hover:text-red-400" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-between">
                    <div>
                      <p className="text-white/50 text-xs">Prix</p>
                      <p className="text-white font-medium">{product.price.toLocaleString('fr-DZ')} DA</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Stock</p>
                      <div className="flex items-center">
                        <span className={`font-medium ${product.stock <= product.lowStockThreshold ? 'text-red-400' : 'text-white'}`}>
                          {product.stock}
                        </span>
                        {product.stock <= product.lowStockThreshold && (
                          <ExclamationCircleIcon className="h-4 w-4 text-red-400 ml-1" />
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">SKU</p>
                      <p className="text-white/80 text-sm">{product.sku}</p>
                    </div>
                  </div>

                  <div className="mt-4 text-sm text-white/60 line-clamp-1">
                    {product.description}
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button 
                      onClick={() => viewProduct(product)}
                      className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Voir détails
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-white/5 backdrop-blur-lg rounded-3xl border border-white/20">
              <ArchiveBoxIcon className="h-16 w-16 text-white/30 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Aucun produit ne correspond à votre recherche</h3>
              <p className="text-white/60 mb-6">Essayez de modifier vos critères de filtrage</p>
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('all');
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
              >
                <ArrowPathIcon className="h-5 w-5" />
                <span>Réinitialiser les filtres</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Products Tab Content */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="w-full md:w-auto flex items-center px-3 py-2 bg-white/10 rounded-xl">
              <MagnifyingGlassIcon className="h-5 w-5 text-white/40" />
              <input
                type="text"
                className="ml-2 bg-transparent outline-none text-white w-full md:w-64"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 bg-white/10 text-white rounded-xl outline-none"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setShowAddForm(true);
                }}
                className="px-3 py-2 bg-blue-600 text-white rounded-xl flex items-center gap-1"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Nouveau produit</span>
              </button>
            </div>
          </div>

          {/* Product List as Table */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl overflow-hidden border border-white/20">
            <div className="overflow-x-auto">
              <table className="w-full min-w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Produit
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Catégorie
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Prix
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-md flex items-center justify-center">
                            <ShoppingBagIcon className="h-6 w-6 text-white/40" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{product.name}</div>
                            <div className="text-xs text-white/50 line-clamp-1">{product.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-500/20 text-blue-300">
                          {categories.find(c => c.value === product.category)?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-white">
                        {product.price.toLocaleString('fr-DZ')} DA
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`text-sm ${product.stock <= product.lowStockThreshold ? 'text-red-400' : 'text-white'}`}>
                            {product.stock}
                          </span>
                          {product.stock <= product.lowStockThreshold && (
                            <ExclamationCircleIcon className="h-4 w-4 text-red-400 ml-1" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-white/80">
                        {product.sku}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button 
                            onClick={() => viewProduct(product)}
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                            title="Voir détails"
                          >
                            <EyeIcon className="h-4 w-4 text-white/70" />
                          </button>
                          <button 
                            onClick={() => editProduct(product)}
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                            title="Modifier"
                          >
                            <PencilIcon className="h-4 w-4 text-white/70" />
                          </button>
                          <button 
                            onClick={() => openDeleteModal(product)}
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 transition-colors"
                            title="Supprimer"
                          >
                            <TrashIcon className="h-4 w-4 text-white/70 hover:text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredProducts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <ShoppingBagIcon className="h-12 w-12 text-white/20" />
                <p className="mt-4 text-white/50 text-lg">Aucun produit ne correspond à votre recherche</p>
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('all');
                  }}
                  className="mt-2 text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories Tab Content */}
      {activeTab === 'categories' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="w-full md:w-64 flex items-center px-3 py-2 bg-white/10 rounded-lg">
              <MagnifyingGlassIcon className="h-5 w-5 text-white/40" />
              <input
                type="text"
                className="ml-2 bg-transparent outline-none text-white w-full"
                placeholder="Rechercher une catégorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              className="px-3 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-1"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nouvelle catégorie</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.filter(c => c.value !== 'all').map((category) => (
              <motion.div
                key={category.value}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <TagIcon className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-white">{category.label}</h3>
                      <p className="text-white/60 text-sm">
                        {mockProducts.filter(p => p.category === category.value).length} produits
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                      <PencilIcon className="h-4 w-4 text-white/70" />
                    </button>
                    <button className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 transition-colors">
                      <TrashIcon className="h-4 w-4 text-white/70 hover:text-red-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <button className="text-blue-400 hover:text-blue-300 text-sm">
                    Voir tous les produits
                  </button>
                </div>
              </motion.div>
            ))}

            {/* Add Category Card */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-dashed border-white/20 hover:border-blue-500/30 transition-all duration-300 flex flex-col items-center justify-center cursor-pointer"
            >
              <div className="p-4 bg-blue-500/10 rounded-full">
                <PlusIcon className="h-8 w-8 text-blue-400" />
              </div>
              <p className="mt-4 text-white/70 text-center">Ajouter une nouvelle catégorie</p>
            </motion.div>
          </div>
        </div>
      )}

      {/* Orders Tab Content */}
      {activeTab === 'orders' && (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/5 backdrop-blur-sm rounded-xl p-4">
            <div className="w-full md:w-auto flex items-center px-3 py-2 bg-white/10 rounded-lg">
              <MagnifyingGlassIcon className="h-5 w-5 text-white/40" />
              <input
                type="text"
                className="ml-2 bg-transparent outline-none text-white w-full md:w-64"
                placeholder="Rechercher une commande..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <select
                className="px-3 py-2 bg-white/10 text-white rounded-lg outline-none"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="processing">En cours</option>
                <option value="shipped">Expédiée</option>
                <option value="delivered">Livrée</option>
                <option value="cancelled">Annulée</option>
              </select>
              <button
                className="px-3 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-1"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Nouvelle commande</span>
              </button>
            </div>
          </div>

          {/* Orders List */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10">
            <div className="overflow-x-auto">
              <table className="w-full min-w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Commande #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Fournisseur
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {[
                    {id: 'CMD-001', date: '2024-07-15', supplier: 'NutriSport', amount: 150000, status: 'delivered', items: 12},
                    {id: 'CMD-002', date: '2024-07-10', supplier: 'SportWear', amount: 85000, status: 'processing', items: 8},
                    {id: 'CMD-003', date: '2024-07-05', supplier: 'FitEquip', amount: 240000, status: 'pending', items: 15},
                    {id: 'CMD-004', date: '2024-07-01', supplier: 'NutriSport', amount: 75000, status: 'shipped', items: 6},
                    {id: 'CMD-005', date: '2024-06-28', supplier: 'SportWear', amount: 45000, status: 'cancelled', items: 4}
                  ].map((order) => (
                    <tr key={order.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{order.id}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-white">{new Date(order.date).toLocaleDateString('fr-FR')}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-white">{order.supplier}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-white">{order.amount.toLocaleString('fr-DZ')} DA</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${order.status === 'delivered' ? 'bg-green-500/20 text-green-300' : 
                          order.status === 'processing' ? 'bg-blue-500/20 text-blue-300' : 
                          order.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                          order.status === 'shipped' ? 'bg-purple-500/20 text-purple-300' :
                          'bg-red-500/20 text-red-300'}`}
                        >
                          {order.status === 'delivered' ? 'Livrée' : 
                           order.status === 'processing' ? 'En cours' :
                           order.status === 'pending' ? 'En attente' :
                           order.status === 'shipped' ? 'Expédiée' :
                           'Annulée'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button 
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                            title="Voir détails"
                          >
                            <EyeIcon className="h-4 w-4 text-white/70" />
                          </button>
                          <button 
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                            title="Modifier"
                          >
                            <PencilIcon className="h-4 w-4 text-white/70" />
                          </button>
                          <button 
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 transition-colors"
                            title="Annuler"
                          >
                            <XMarkIcon className="h-4 w-4 text-white/70 hover:text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && productToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <TrashIcon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Supprimer ce produit</h3>
              <p className="text-white/70 mb-6">
                Êtes-vous sûr de vouloir supprimer le produit {productToDelete.name} ? Cette action est irréversible.
              </p>
              <div className="flex gap-3 w-full">
                <button 
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleDeleteProduct(productToDelete.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Product Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                {selectedProduct ? 'Modifier le produit' : 'Ajouter un produit'}
              </h2>
              <button 
                onClick={() => setShowAddForm(false)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6 text-white/70" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Formulaire à implémenter */}
              <p className="text-white/70">Formulaire de gestion des produits à implémenter</p>
              
              <div className="flex justify-end mt-6 gap-2">
                <button 
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleSaveProduct({})}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && !showAddForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                Détails du produit
              </h2>
              <button 
                onClick={() => setSelectedProduct(null)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6 text-white/70" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white">{selectedProduct.name}</h3>
                  <p className="text-white/60">
                    {categories.find(c => c.value === selectedProduct.category)?.label}
                  </p>
                </div>
                
                <div className="bg-white/5 p-4 rounded-lg">
                  <p className="text-white/60 mb-2">{selectedProduct.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-white/50 text-xs">SKU</p>
                      <p className="text-white">{selectedProduct.sku}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Code-barres</p>
                      <p className="text-white">{selectedProduct.barcode}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-white/50 text-xs">Prix de vente</p>
                      <p className="text-white font-medium">{selectedProduct.price.toLocaleString('fr-DZ')} DA</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Prix d'achat</p>
                      <p className="text-white">{selectedProduct.costPrice.toLocaleString('fr-DZ')} DA</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Marge</p>
                      <p className="text-green-400">
                        {Math.round((selectedProduct.price - selectedProduct.costPrice) / selectedProduct.costPrice * 100)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Bénéfice par unité</p>
                      <p className="text-green-400">
                        {(selectedProduct.price - selectedProduct.costPrice).toLocaleString('fr-DZ')} DA
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-white/50 text-xs">Stock actuel</p>
                      <p className={`font-medium ${selectedProduct.stock <= selectedProduct.lowStockThreshold ? 'text-red-400' : 'text-white'}`}>
                        {selectedProduct.stock} unités
                      </p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Seuil d'alerte</p>
                      <p className="text-white">{selectedProduct.lowStockThreshold} unités</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Emplacement</p>
                      <p className="text-white">{selectedProduct.location}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Fournisseur</p>
                      <p className="text-white">{selectedProduct.supplier}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/5 p-4 rounded-lg">
                  <p className="text-white/50 text-xs mb-2">Valeur du stock</p>
                  <p className="text-xl font-medium text-white">
                    {(selectedProduct.stock * selectedProduct.price).toLocaleString('fr-DZ')} DA
                  </p>
                  <p className="text-white/60 text-sm mt-1">
                    Coût d'achat: {(selectedProduct.stock * selectedProduct.costPrice).toLocaleString('fr-DZ')} DA
                  </p>
                </div>
                
                <div className="bg-white/5 p-4 rounded-lg">
                  <p className="text-white/50 text-xs mb-2">Actions rapides</p>
                  <div className="flex gap-2 flex-wrap">
                    <button className="px-3 py-2 bg-blue-600/30 text-blue-300 rounded-lg hover:bg-blue-600/50 flex items-center gap-1 text-sm">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span>Entrée stock</span>
                    </button>
                    <button className="px-3 py-2 bg-amber-600/30 text-amber-300 rounded-lg hover:bg-amber-600/50 flex items-center gap-1 text-sm">
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      <span>Sortie stock</span>
                    </button>
                    <button className="px-3 py-2 bg-green-600/30 text-green-300 rounded-lg hover:bg-green-600/50 flex items-center gap-1 text-sm">
                      <QrCodeIcon className="h-4 w-4" />
                      <span>Imprimer code-barres</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end mt-6">
              <button 
                onClick={() => {
                  setSelectedProduct(null);
                  editProduct(selectedProduct);
                }}
                className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 flex items-center gap-1"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Modifier</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockPage; 